import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import type { TripItineraryData, TripStop } from 'tripPlanner/api/types'
import { getOfferItineraryDayRange, findOfferStopInItinerary, findNearestStop, type Coordinates } from 'tripPlanner/utils/chatItinerary'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import noop from 'lib/function/noop'

type ItineraryStatus =
  | { type: 'not-added' }
  | { type: 'to-be-planned'; label: string }
  | { type: 'days'; label: string }

export interface SendMessageOptions {
  offerId?: string
  offerType?: string
}

interface Args {
  itineraryData?: TripItineraryData
  hotelName: string
  offerId: string
  offerType?: string
  hotelCity?: string
  hotelCoordinates?: Coordinates
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onAfterAction?: () => void
  filterNights?: number
}

interface Result {
  handleAddToTrip: () => void
  isModalOpen: boolean
  closeModal: () => void
  itineraryStatus: ItineraryStatus
  relevantStop?: TripStop
  defaultDayNumber: number
  defaultNights: number
}

const NOT_ADDED_STATUS: ItineraryStatus = { type: 'not-added' }

function useAddHotelToTrip({
  itineraryData,
  hotelName,
  offerId,
  offerType,
  hotelCity,
  hotelCoordinates,
  onSendMessage = noop,
  onAfterAction,
  filterNights,
}: Args): Result {
  const intl = useIntl()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const offerDayRange = useMemo(
    () => getOfferItineraryDayRange(offerId, itineraryData),
    [offerId, itineraryData],
  )

  const itineraryStatus = useMemo<ItineraryStatus>(() => {
    if (!offerDayRange) return NOT_ADDED_STATUS
    return { type: offerDayRange.type, label: offerDayRange.label }
  }, [offerDayRange])

  const defaultDayNumber = offerDayRange?.firstDayNumber ?? 1
  const defaultNights = offerDayRange?.nightsCount ?? filterNights ?? 1

  const relevantStop = useMemo<TripStop | undefined>(() => {
    const offerStop = findOfferStopInItinerary(offerId, itineraryData)
    if (offerStop) return offerStop

    if (!itineraryData?.stops) return undefined
    return findNearestStop(hotelCoordinates, itineraryData.stops)
  }, [offerId, itineraryData, hotelCoordinates])

  const handleAddToTrip = useCallback(() => {
    const stops = itineraryData?.stops ?? []
    const isAlreadyInTrip = itineraryStatus.type !== 'not-added'

    if (isAlreadyInTrip) {
      setIsModalOpen(true)
      return
    }

    if (stops.length === 0) {
      const displayMessage = hotelCity
        ? intl.formatMessage(
            {
              id: 'RMB55Nt73KQmOgLYwQ6B',
              defaultMessage: 'Add "{hotelName}" to be planned in {cityName}',
              description: 'User chat message shown when adding a hotel to be planned in a specific city',
            },
            {
              hotelName,
              cityName: hotelCity,
            },
          )
        : intl.formatMessage({
            id: '9EdNRsLnq6gg6QYmWJW-',
            defaultMessage: 'Add "{hotelName}" to be planned',
            description: 'User chat message shown when adding a hotel to the unscheduled part of a trip with no stops',
          }, { hotelName })
      const cityLabel = hotelCity ? ` in ${hotelCity}` : ''
      onSendMessage(
        appendContextTag(
          displayMessage,
          `Add "${hotelName}" to be planned${cityLabel}`,
          TRIP_PLANNER_ACTION_CONTEXT_LABEL,
        ),
        { offerId, offerType },
      )
      onAfterAction?.()
      return
    }

    const hasAnyDays = stops.some(stop => stop.days.length > 0)
    if (!hasAnyDays) {
      const targetStop = relevantStop ?? stops[0]
      onSendMessage(
        appendContextTag(
          intl.formatMessage(
            {
              id: 'qmRNkszmMJ0jG9qinud4',
              defaultMessage: 'Add "{hotelName}" to be planned for {stopName}',
              description: 'User chat message shown when adding a hotel to the unscheduled part of a stop with no dated days',
            },
            {
              hotelName,
              stopName: targetStop.placeName,
            },
          ),
          `Add "${hotelName}" to be planned for ${targetStop.placeName}`,
          TRIP_PLANNER_ACTION_CONTEXT_LABEL,
        ),
        { offerId, offerType },
      )
      onAfterAction?.()
      return
    }

    setIsModalOpen(true)
  }, [intl, itineraryData?.stops, itineraryStatus.type, hotelName, hotelCity, offerId, offerType, onSendMessage, onAfterAction, relevantStop])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  return {
    handleAddToTrip,
    isModalOpen,
    closeModal,
    itineraryStatus,
    relevantStop,
    defaultDayNumber,
    defaultNights,
  }
}

export default useAddHotelToTrip
